import { cloneElement, isValidElement, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { capturePayment } from "pages/GenericOrderDetailsPage/apis";
import type { CounterPaymentMethod } from "service/moneeflo/legacy-forex-order-api";
import DeskDialog, { Field, inputClass } from "pages/GenericOrderDetailsPage/components/DeskDialog";
import FilePicker from "pages/GenericOrderDetailsPage/components/FilePicker";
import { formatInrMinor, inputToMinor } from "pages/GenericOrderDetailsPage/utils/money";

/** What the customer paid with, as core's payment methods. */
const MODES: { value: string; label: string; method: CounterPaymentMethod }[] = [
  { value: "cash", label: "Cash", method: "cash" },
  { value: "upi", label: "UPI", method: "upi" },
  { value: "bank_transfer", label: "Bank transfer", method: "neft" },
  { value: "cheque", label: "Cheque", method: "cheque" },
  { value: "demand_draft", label: "Demand draft", method: "demand_draft" },
];

interface PaymentDialogProps {
  children?: React.ReactNode;
  /** What is still owed, in paise, as core says. */
  maxMinor?: string;
}

/** Recording a payment taken at the counter: how it was paid, its reference, the amount and the proof. */
export function PaymentDialog({ children, maxMinor }: PaymentDialogProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("");
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState("");
  const [proof, setProof] = useState<File | null>(null);
  const [supporting, setSupporting] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      setMode("");
      setReference("");
      setAmount("");
      setProof(null);
      setSupporting(null);
      setError(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: capturePayment,
    onSuccess: () => {
      toast.success("Payment recorded");
      setOpen(false);
      queryClient.invalidateQueries(["orderDetails"]);
    },
    onError: (e: unknown) => {
      // The payment may have been recorded even when a document was not
      // attached, so the list is read again either way.
      queryClient.invalidateQueries(["orderDetails"]);
      setError(e instanceof Error ? e.message : "The payment could not be recorded.");
    },
  });

  const minor = inputToMinor(amount);
  const overMax = minor !== null && maxMinor !== undefined && minor > BigInt(maxMinor);
  const ready = mode !== "" && reference.trim().length > 0 && minor !== null && minor > 0n && !overMax && !!proof;
  const chosen = MODES.find((m) => m.value === mode);

  return (
    <>
      {isValidElement(children)
        ? cloneElement(children as React.ReactElement<{ onClick?: () => void }>, { onClick: () => setOpen(true) })
        : children}
      <DeskDialog
        open={open}
        onOpenChange={setOpen}
        title="Record a payment"
        description="Enter what the customer paid at the counter. It counts once it is saved."
        busy={mutation.isLoading}
        submitLabel="Save payment"
        submitDisabled={!ready}
        closeLabel="Cancel"
        error={error}
        onSubmit={() => {
          if (!chosen || minor === null || !proof) return;
          setError(null);
          mutation.mutate({ method: chosen.method, reference, amountMinor: minor.toString(), proof, supporting });
        }}
      >
        <Field label="Payment mode" htmlFor="pay-mode">
          <select id="pay-mode" className={inputClass} value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="">Choose how it was paid</option>
            {MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Bank reference / UTR" htmlFor="pay-reference" hint="The UTR, cheque or draft number, or receipt number.">
          <input id="pay-reference" className={inputClass} value={reference} onChange={(e) => setReference(e.target.value)} autoComplete="off" />
        </Field>
        <Field
          label="Amount (₹)"
          htmlFor="pay-amount"
          hint={
            mode === "cash"
              ? `Cash is limited by the cash rule; the system refuses an amount over it.${maxMinor ? ` Still owed: ${formatInrMinor(maxMinor)}.` : ""}`
              : maxMinor
                ? `Still owed: ${formatInrMinor(maxMinor)}.`
                : undefined
          }
        >
          <input
            id="pay-amount"
            inputMode="decimal"
            className={inputClass}
            value={amount}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) setAmount(v);
            }}
            aria-invalid={overMax}
            autoComplete="off"
          />
          {overMax && <p role="alert" className="text-sm text-[#B00020]">That is more than is owed.</p>}
        </Field>
        <Field label="Payment proof" htmlFor="pay-proof" hint="A photo or PDF of the receipt or slip.">
          <FilePicker id="pay-proof" file={proof} onChange={setProof} />
        </Field>
        <Field label="Supporting document (optional)" htmlFor="pay-supporting" hint="For example, when the payer's name does not match.">
          <FilePicker id="pay-supporting" file={supporting} onChange={setSupporting} />
        </Field>
      </DeskDialog>
    </>
  );
}
